import {
  BaseTheme,
  BaseThemeType,
  Box,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Grid,
  styled,
  Typography,
} from 'opensolar-ui'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import DistributorChips from './DistributorsChip'

import {
  DistributorTradePricingContext,
  DistributorTradePricingContextType,
} from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import ExpoTooltip from 'expo/ExpoTooltip'
import { StockLevelTypeV2 } from 'opensolar-common'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { permissionsSelectors } from 'ducks/permissions'
import AvailableStocksButton from 'elements/hardwareSelectorV2/elements/AvailableStocksButton'
import {
  getStockStatusMessagesPerDistributor,
  STOCK_LEVEL_SCENARIO,
} from 'elements/hardwareSelectorV2/elements/stockUtils'
import { getComponentRating } from 'elements/hardwareSelectorV2/elements/utility'
import useTotalStockAcrossSuppliers from 'elements/hardwareSelectorV2/useTotalStockAcrossSuppliers'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import lodash from 'lodash'

import IndicativePriceLabel from 'elements/IndicativePriceLabel'
import TieredPriceOnApplicationPricingLabel from 'elements/TieredPriceOnApplicationPricingLabel'
import { useAddedToCartToast } from 'pages/ordering/addedToCartToast'
import { useTranslate } from 'ra-core'
import { shouldShowDistributorPricing } from 'services/useDistributorTradePricing'
import { OpenSolarPalette } from 'Themes'
import { ActionType } from 'types/tracking'
import { orgSelectors } from '../../../ducks/orgs'
import { BulkOptionType, ComponentLikeType, DistributorDataTypeV2 } from '../../../elements/hardwareSelectorV2/types'
import PriceOnApplicationPricingLabel from '../../../elements/PriceOnApplicationPricingLabel'
import { useGlobalCart } from '../../../hooks/useGlobalCart'
import { format_percentage_value, formatCurrencyWithSymbol, formatDecimalPlaces } from '../../../util/misc'
import { STOCK_LEVEL_STATUS } from '../constants'
import DealBadge from '../deals/DealBadge'
import { computeDealPrice, filterProductLevelDeals } from '../deals/dealEngine'
import BulkPricingText from '../detail/content/BulkPricingText'
import { BulkMessage } from '../detail/content/ProductDetailCheckoutMultiDistributor'
import SupplierConnectionLink from '../home/DistributorConnection/SupplierConnectionLink'
import useBulkOptions from '../hooks/useBulkOptions'
import useIsIndicativePricing from '../hooks/useIsIndicativePricing'
import useIsPriceOnApplication from '../hooks/useIsPriceOnApplication'
import useIsTieredPriceOnApplication from '../hooks/useIsTieredPriceOnApplication'
import OrderLineItem from '../OrderLineItem'
import { sortByPriceOrderingV2 } from '../utils'
import ActionButton from './ActionButton'
import { ProductTileLayout } from './ProductTileLayout'

const ImageContainer = styled('div', { name: 'ImageContainer' })({
  position: 'relative',
  width: '100%',
  height: '160px',
})

const ImageOverlay = styled('div', { name: 'ImageOverlay' })({
  position: 'relative',
  zIndex: 10,
})

const ProductImageBg = styled('div', { name: 'ProductImageBg' })(({ theme }: { theme: BaseThemeType }) => ({
  background: theme.palette.neutralGrey[200],
  position: 'absolute',
  inset: 0,
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}))

const ProductImage = styled('img', { name: 'ProductImage' })({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '160px',
  objectFit: 'contain',
})

const ImageOverlayFirstRow = styled('div', { name: 'ImageOverlayFirstRow' })({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
})

const ShortDescription = styled('span', { name: 'ShortDescription' })({
  width: '100%',
  '& p': {
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    margin: 0,
    wordBreak: 'break-word',
  },
})

const ConnectLinkWrapper = styled('div', { name: 'ConnectLinkWrapper' })({
  minHeight: '27px',
})

const BulkPricingTextWrapper = styled(Grid, { name: 'BulkPricingTextWrapper' })({
  width: '100%',
})

const StockMessageContainer = styled(Box, { name: 'StockMessageContainer' })({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'flex-start',
  gap: 6,
  ...BaseTheme.typography.body2,
})

const StockMessageTitle = styled('div', { name: 'StockMessageTitle' })({
  ...BaseTheme.typography.body2,
})

const DistributorRowContainer = styled(Box, { name: 'DistributorRowContainer' })({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

export const ComponentPlaceHolderImage = () => (
  <ProductImageBg>
    <img src={`${window.PUBLIC_URL}/images/component-image-placeholder.png`} alt="" />
  </ProductImageBg>
)

interface PricingProps {
  selectedDistributor?: DistributorDataTypeV2 | null
  displayDetailsDistributor?: DistributorDataTypeV2 | null
  totalCost?: number
  currencySymbol: string
  showPricingForSelectedDistributor?: boolean
  isIndicativePricing?: boolean
  discountPercentValue?: number
  pricePerWatt?: string | null
  hideSubtext?: boolean
  dealPrice: number
}

const DiscountBadge = styled(Typography, { name: 'ProductTilePricingTextContainer' })(({ theme }) => ({
  backgroundColor: theme.palette.green[200],
  color: theme.palette.green[1000],
  padding: '0px 4px',
  borderRadius: 4,
}))

const PricingContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}))

const PricingRowContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
}))

export const PricingText = ({
  selectedDistributor,
  displayDetailsDistributor,
  totalCost,
  currencySymbol,
  showPricingForSelectedDistributor,
  isIndicativePricing = false,
  discountPercentValue,
  pricePerWatt,
  dealPrice,
  hideSubtext = false,
}: PricingProps) => {
  const price = Math.min(dealPrice, displayDetailsDistributor?.price ?? 0)
  const formattedPrice = formatCurrencyWithSymbol(totalCost ?? price, currencySymbol)
  const translate = useTranslate()
  if (!selectedDistributor) {
    return (
      <Typography variant="h6">
        {translate('From')} {formattedPrice}
      </Typography>
    )
  }

  const priceBeforeDiscount = discountPercentValue
    ? formatCurrencyWithSymbol(price / (1 - discountPercentValue), currencySymbol)
    : null

  const isShowingDiscount = Boolean(discountPercentValue) && showPricingForSelectedDistributor

  return (
    <Grid container>
      <Grid container item alignItems="center" justifyContent="center" spacing={1}>
        <Grid item>
          <Typography {...(isShowingDiscount ? { colorHex: BaseTheme.palette.red[800] } : {})} variant="h6">
            {showPricingForSelectedDistributor
              ? `${isIndicativePricing ? '~' : ''}${formattedPrice}`
              : `${currencySymbol} -`}
          </Typography>
        </Grid>
        {isShowingDiscount && (
          <>
            <Grid item>
              <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                {priceBeforeDiscount}
              </Typography>
            </Grid>
            <Grid item>
              <DiscountBadge variant="body3">
                {translate('Save')} {formatDecimalPlaces(discountPercentValue! * 100, 0)}%
              </DiscountBadge>
            </Grid>
          </>
        )}
      </Grid>
      {!hideSubtext && showPricingForSelectedDistributor && (
        <Grid item>
          <Typography color={BaseTheme.palette.grey[1000]} variant="body3">
            {pricePerWatt} ({displayDetailsDistributor?.distributor_name})
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}

const getQuantity = (arr?: StockLevelTypeV2[]): StockLevelTypeV2 | null => {
  if (!arr) {
    return null
  }
  return arr.find(({ quantity }) => quantity) || null
}

export const hasCatalogPrice = (price: number | null | undefined): boolean =>
  typeof price === 'number' && Number.isFinite(price) && price > 0

// Sharable logic between ProductTile and ProductDetailCheckoutMultiDistributor
// There are many more improvements that could be made here.
export const useProductTileState = ({ component }: { component: ComponentLikeType }) => {
  const translate = useTranslate()
  const [selectedDistributor, setSelectedDistributor] = useState<DistributorDataTypeV2 | null>(null)
  const [hasSelectedDistributor, setHasSelectedDistributor] = useState(false)
  const countryIso2 = useSelector(orgSelectors.getOrgIso2)

  const { orderableLineItems } = useGlobalCart()

  const distributorTradePricing =
    useContext<DistributorTradePricingContextType>(DistributorTradePricingContext).distributorTradePricing

  const showConnectDistributorLink = useMemo(() => {
    if (!selectedDistributor) {
      return false
    }
    return !shouldShowDistributorPricing(selectedDistributor?.distributor, distributorTradePricing)
  }, [selectedDistributor, distributorTradePricing])

  // Only include pricing for distributors where it should be shown.
  const lowestPriceDistributor = component
    ? sortByPriceOrderingV2(component?.ordering_v2)
        .filter((distributor) => shouldShowDistributorPricing(distributor.distributor, distributorTradePricing))
        .find((ordering) => ordering.price)
    : undefined

  const displayDetailsDistributor = selectedDistributor || lowestPriceDistributor

  const lineItemForComponent = orderableLineItems?.find(
    (lineItem: OrderLineItem) =>
      lineItem.code === component?.code && lineItem.selectedDistributor === displayDetailsDistributor?.distributor
  )
  const quantityRequired = lineItemForComponent?.quantity || 0

  const hasIncomingStocks = selectedDistributor?.stock_levels?.filter((stock) => !stock.in_stock && stock.planned_date)

  const stockLevel: StockLevelTypeV2 | null =
    getQuantity(selectedDistributor?.stock_levels) || getQuantity(lowestPriceDistributor?.stock_levels)

  const hasStocks = stockLevel?.quantity && Number(stockLevel.quantity) > 0

  const currencySymbol = useMemo(() => {
    return component?.ordering_v2.find((order) => !!order.currency_symbol)?.currency_symbol ?? ''
  }, [component])

  const { availableQuantity, isGlobalOutOfStock, showIsInStockElsewhere } = useTotalStockAcrossSuppliers(
    component?.ordering_v2 ?? [],
    selectedDistributor?.distributor
  )

  const stockLevelLabel = useMemo(() => {
    const stockLevelStatusPerDistributor = getStockStatusMessagesPerDistributor({
      distributors: component?.ordering_v2,
      availableQuantity,
      selectedDistributor,
      quantityRequired,
      translate,
    })
    const stockLevelStatusForSelectedDistributor = stockLevelStatusPerDistributor?.find(
      ({ distributor }) => distributor === displayDetailsDistributor?.distributor
    )

    return stockLevelStatusForSelectedDistributor
  }, [
    component?.ordering_v2,
    displayDetailsDistributor,
    quantityRequired,
    selectedDistributor,
    availableQuantity,
    translate,
  ])

  const onSetDistributor = (distributor: DistributorDataTypeV2 | null) => {
    setSelectedDistributor(distributor)
    setHasSelectedDistributor(distributor !== null)
  }

  useEffect(() => {
    if (component?.ordering_v2.length === 1) {
      onSetDistributor(component.ordering_v2[0])
    }
  }, [])

  const distinctDistributors = useMemo(() => {
    return lodash.uniq(component?.ordering_v2.map((distributor) => distributor.distributor))
  }, [component?.ordering_v2])

  const visibleDeals = showConnectDistributorLink ? [] : (displayDetailsDistributor?.deals ?? [])

  const productDeals = filterProductLevelDeals(visibleDeals)

  const dealPrice = computeDealPrice(displayDetailsDistributor?.price ?? 0, productDeals)

  const tradePrice = displayDetailsDistributor?.price ?? 0

  const discountPercentValue = useMemo(() => {
    const tradeDiscountFraction = displayDetailsDistributor?.price_detail?.discount_value ?? 0
    if (dealPrice > tradePrice) return tradeDiscountFraction
    const originalPrice = tradeDiscountFraction > 0 ? tradePrice / (1 - tradeDiscountFraction) : tradePrice
    if (!originalPrice || originalPrice <= 0) return tradeDiscountFraction
    return (originalPrice - dealPrice) / originalPrice
  }, [dealPrice, displayDetailsDistributor, tradePrice])

  const pricePerWatt = useMemo(() => {
    const price = Math.min(dealPrice, tradePrice)
    const componentData =
      component && Object.keys(component).includes('data') && typeof component['data'] === 'string'
        ? JSON.parse(component['data'])
        : undefined
    if (!componentData) return null
    const componentRating = getComponentRating(component.type, componentData)
    if (componentRating === '-' || typeof componentRating.value !== 'number') return null
    const pricePerUnit = price / componentRating.value
    if (pricePerUnit < 1) {
      const subUnit = countryIso2 === 'GB' ? 'p' : 'c'
      return `${format_percentage_value(pricePerUnit, 2)}${subUnit}/${componentRating.suffix}`
    }
    return `${currencySymbol}${formatDecimalPlaces(pricePerUnit, 2)}/${componentRating.suffix}`
  }, [currencySymbol, countryIso2, dealPrice, tradePrice])

  return {
    selectedDistributor, // Always has a value
    hasSelectedDistributor, // Ever been clicked?
    onSetDistributor,
    distributorTradePricing,
    showConnectDistributorLink,
    lowestPriceDistributor,
    displayDetailsDistributor,
    stockLevel,
    discountPercentValue,
    hasStocks,
    hasIncomingStocks,
    currencySymbol,
    totalQuantityAvailable: availableQuantity,
    stockLevelLabel,
    distinctDistributors,
    isGlobalOutOfStock,
    showIsInStockElsewhere,
    dealPrice,
    pricePerWatt,
  }
}

const RED = BaseTheme.palette.red[800]
const BLUE = BaseTheme.palette.blue[1000]
const GREEN = BaseTheme.palette.green[600]

const ProductTile = ({ component, liteMode = false }: { component: ComponentLikeType; liteMode?: boolean }) => {
  const translate = useTranslate()
  const history = useHistory()
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'product_tile',
    eventName: 'Hardware Product',
  })

  const { addLineItemFromV2Component, updateLineItemQty, removeLineItem, orderableLineItems, tryCollapseAccordion } =
    useGlobalCart()
  const { showAddedToCartToast, toastOpen } = useAddedToCartToast(liteMode)

  const {
    selectedDistributor,
    onSetDistributor,
    distributorTradePricing,
    showConnectDistributorLink,
    displayDetailsDistributor,
    discountPercentValue,
    currencySymbol,
    totalQuantityAvailable,
    stockLevelLabel,
    showIsInStockElsewhere,
    dealPrice,
    pricePerWatt,
  } = useProductTileState({ component })

  const selectedSupplier =
    displayDetailsDistributor?.distributor && displayDetailsDistributor.distributor !== 'component'
      ? (displayDetailsDistributor.distributor as any)
      : undefined
  const isIndicativePricing = useIsIndicativePricing(selectedSupplier)
  const isPriceOnApplication = useIsPriceOnApplication(selectedSupplier)
  const isTieredPriceOnApplication = useIsTieredPriceOnApplication(selectedSupplier, displayDetailsDistributor?.price)

  const {
    isBulkAvailable,
    lowestBulkPrice,
    bulkPriceDistributorNames,
    selectedDistributorLowestBulkPrice,
    bulkOptions,
    bulkPriceMessage,
  } = useBulkOptions({
    component,
    selectedDistributor: displayDetailsDistributor,
  })

  const [cartItem, setCartItem] = useState<OrderLineItem | null>(null)
  const [isImageLinkBroken, setIsImageLinkBroken] = useState(false)
  const isAU = useSelector(orgSelectors.getOrgIso2) === 'AU'
  const [bulkOption, setBulkOption] = useState<BulkOptionType | null>(null)
  const [bulkQuantity, setBulkQuantity] = useState<number>(1)
  const { allowView: allowViewPricing } = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'component_pricing_costing')
  )
  const stockLocationModalRef = useRef<HTMLDivElement>(null)
  const bundleDealModalRef = useRef<HTMLDivElement>(null)

  const titleColor = useMemo(() => {
    if (selectedDistributor?.distributor === 'outlet') {
      if (
        [STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER, STOCK_LEVEL_STATUS.AVAILABLE].includes(
          stockLevelLabel?.status || ''
        )
      ) {
        return GREEN
      } else {
        return RED
      }
    }
    if (
      [STOCK_LEVEL_SCENARIO.PARTIALLY_IN_STOCK, STOCK_LEVEL_SCENARIO.OUT_OF_STOCK_WITH_INCOMING_SCHEDULE].includes(
        stockLevelLabel?.scenario || ''
      )
    ) {
      return RED
    } else if (
      selectedDistributor &&
      stockLevelLabel?.scenario === STOCK_LEVEL_SCENARIO.STOCK_AVAILABILITY_UPON_REQUEST
    ) {
      return BLUE
    }
    return totalQuantityAvailable && Number(totalQuantityAvailable) > 0 ? GREEN : RED
  }, [totalQuantityAvailable, stockLevelLabel?.scenario, selectedDistributor?.distributor])

  const handleAddToCart = (quantity?: number, bulkOption?: BulkOptionType | null) => {
    if (!displayDetailsDistributor || Object.keys(displayDetailsDistributor).length === 0) {
      return
    }
    const qty = quantity ?? bulkQuantity ?? 1
    const unitPrice = displayDetailsDistributor.price_detail?.price_ex_vat ?? 0
    addLineItemFromV2Component(component, displayDetailsDistributor, quantity, bulkOption)

    // If we add the item without having selected a distributor, we should set the distributor
    // to be identical to displayDetailsDistributor.
    if (!selectedDistributor) {
      onSetDistributor(displayDetailsDistributor)
    }

    const imageUrl = displayDetailsDistributor?.image_url || component.ordering_v2?.find((o) => o.image_url)?.image_url
    showAddedToCartToast({
      variant: 'item',
      item: {
        description: displayDetailsDistributor?.short_description ?? component.name,
        code: component.code,
        imageUrl,
        quantity: qty,
        price: Math.min(dealPrice, unitPrice),
        distributor: displayDetailsDistributor?.distributor,
      },
    })
    trackUserInteraction(
      { type: ActionType.CLICK },
      {
        source: 'add_to_cart_button',
        context: 'product_tile',
      }
    )
  }

  const handleQuantityChange = (quantity: number) => {
    if (quantity === 0) {
      trackUserInteraction(
        { type: ActionType.CLICK },
        { action: 'removed', source: 'quantity_field', context: 'product_tile' }
      )
      removeLineItem(cartItem?.uuid)
      setCartItem(null)
      // collapse the component type in global cart
      cartItem?.uuid && tryCollapseAccordion(cartItem.uuid)
      return
    }
    if (!cartItem?.uuid) return
    updateLineItemQty(cartItem.uuid, quantity)
    // Show or refresh the toast so it reflects the new quantity
    const unitPrice = displayDetailsDistributor?.price_detail?.price_ex_vat ?? cartItem?.pricePerUnit ?? 0
    const imageUrl =
      displayDetailsDistributor?.image_url ||
      component.ordering_v2?.find((o: { image_url?: string }) => o.image_url)?.image_url
    showAddedToCartToast({
      variant: 'item',
      item: {
        description: displayDetailsDistributor?.short_description ?? component.name,
        code: component.code,
        imageUrl,
        quantity,
        price: Math.min(dealPrice, unitPrice),
        distributor: displayDetailsDistributor?.distributor,
      },
    })
  }

  const handleClick = (event?: React.MouseEvent<HTMLDivElement>) => {
    if (
      stockLocationModalRef.current?.contains(event?.target as Node) ||
      bundleDealModalRef.current?.contains(event?.target as Node)
    ) {
      return
    }
    history.push('/shop/product/' + encodeURIComponent(component.code))
    document.querySelector<HTMLElement>('.content')?.scrollTo(0, 0)
  }

  const handleDistributorSelect = (event, distributor) => {
    trackUserInteraction(
      { type: ActionType.SELECT },
      {
        action: 'clicked',
        source: 'select_available_distributor',
        context: 'product_tile',
        distributor: distributor?.distributor,
      }
    )
    setBulkOption(null)
    setBulkQuantity(1)
    if (selectedDistributor === distributor && component.ordering_v2.length > 1) {
      onSetDistributor(null)
    } else {
      onSetDistributor(distributor)
    }
  }

  useEffect(() => {
    const cartOrderItem = orderableLineItems.find(
      (lineItem: OrderLineItem) =>
        lineItem.code === component.code && lineItem.selectedDistributor === displayDetailsDistributor?.distributor
    )
    if (!cartOrderItem) {
      setCartItem(null)
      return
    }
    setCartItem(cartOrderItem)
    // Re-sync when toast closes so cartItem is fresh after add-to-cart toast is gone
  }, [orderableLineItems, displayDetailsDistributor, toastOpen])

  useEffect(() => {
    if (
      component?.ordering_v2?.length === 1 &&
      component.ordering_v2[0].distributor !== selectedDistributor?.distributor &&
      shouldShowDistributorPricing(component.ordering_v2[0].distributor, distributorTradePricing)
    ) {
      onSetDistributor(component.ordering_v2[0])
    }
  }, [component.ordering_v2])

  const defaultStockMessage = useMemo(() => {
    if (totalQuantityAvailable === 0 && isAU) {
      return translate('Out of stock')
    }

    return translate('In Stock')
  }, [totalQuantityAvailable, isAU, translate])

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <ProductTileLayout
        onClick={handleClick}
        liteMode={liteMode}
        Thumb={
          <ImageContainer>
            <ImageOverlay>
              <ImageOverlayFirstRow>
                {component.partner_expo_stand && <ExpoTooltip {...component.partner_expo_stand} />}
              </ImageOverlayFirstRow>

              {allowViewPricing && isBulkAvailable && <BulkMessage>{bulkPriceMessage}</BulkMessage>}
            </ImageOverlay>

            {!isImageLinkBroken && displayDetailsDistributor?.image_url ? (
              <ProductImage
                src={displayDetailsDistributor?.image_url}
                alt="Product"
                onError={() => setIsImageLinkBroken(true)}
              />
            ) : (
              <ComponentPlaceHolderImage />
            )}
          </ImageContainer>
        }
        DescriptionRow={
          <>
            <Typography textVariant="body1" colorHex={OpenSolarPalette.greyMid1}>
              {component.code}
            </Typography>
            <ShortDescription title={displayDetailsDistributor?.short_description}>
              <Typography textVariant="body1">{displayDetailsDistributor?.short_description}</Typography>
            </ShortDescription>
          </>
        }
        PricingRow={
          <>
            {!isPriceOnApplication &&
              !isTieredPriceOnApplication &&
              displayDetailsDistributor?.price &&
              allowViewPricing && (
                <PricingContainer>
                  {!liteMode && (
                    <DealBadge
                      orderingV2={component.ordering_v2 ?? []}
                      selectedDistributor={selectedDistributor?.distributor}
                      currentProductCode={component.code}
                      bundleDealModalRef={bundleDealModalRef}
                    />
                  )}

                  <PricingRowContainer data-testid="product-tile-price">
                    <PricingText
                      selectedDistributor={selectedDistributor}
                      displayDetailsDistributor={displayDetailsDistributor}
                      totalCost={bulkOption?.price}
                      currencySymbol={currencySymbol}
                      showPricingForSelectedDistributor={selectedDistributor !== null && !showConnectDistributorLink}
                      isIndicativePricing={isIndicativePricing}
                      discountPercentValue={discountPercentValue}
                      dealPrice={dealPrice}
                      pricePerWatt={pricePerWatt}
                    />
                    {isIndicativePricing && <IndicativePriceLabel />}
                  </PricingRowContainer>
                </PricingContainer>
              )}
          </>
        }
        BulkRow={
          allowViewPricing &&
          isBulkAvailable && (
            <BulkPricingTextWrapper item>
              <BulkPricingText
                lowestPrice={lowestBulkPrice}
                distributorNames={bulkPriceDistributorNames}
                currencySymbol={currencySymbol}
                selectedDistributorLowestBulkPrice={selectedDistributorLowestBulkPrice}
                bulkOption={bulkOption}
                isDistributorSelected={selectedDistributor !== null}
              />
            </BulkPricingTextWrapper>
          )
        }
        DistributorRow={
          <DistributorRowContainer>
            <Typography textVariant="helper1" colorHex={OpenSolarPalette.greyMid1}>
              {`${translate('SELECT SUPPLIER')}`}
            </Typography>
            <DistributorChips
              ordering_v2={component.ordering_v2}
              onSelect={handleDistributorSelect}
              selectedDistributor={selectedDistributor}
            />
          </DistributorRowContainer>
        }
        ConnectRow={
          <ConnectLinkWrapper>
            {!showConnectDistributorLink && hasCatalogPrice(displayDetailsDistributor?.price) && (
              <>
                <StockMessageContainer color={titleColor}>
                  <StockMessageTitle>
                    {selectedDistributor ? stockLevelLabel?.productTileStockMessage.title : defaultStockMessage}
                  </StockMessageTitle>
                  <AvailableStocksButton
                    data={component.ordering_v2}
                    selectedDistributor={selectedDistributor?.distributor}
                    ref={stockLocationModalRef}
                    inStockElsewhere={showIsInStockElsewhere}
                    productCode={component.code}
                    productName={displayDetailsDistributor?.short_description ?? component.name}
                    amplitudeEventType="Hardware Product"
                  />
                </StockMessageContainer>
                {stockLevelLabel?.productTileStockMessage.subtitle && (
                  <div style={{ fontSize: '12px', color: OpenSolarPalette.greyMid1 }}>
                    {stockLevelLabel?.productTileStockMessage.subtitle}
                  </div>
                )}
              </>
            )}
            {isPriceOnApplication && (
              <Box marginTop={1}>
                <PriceOnApplicationPricingLabel />
              </Box>
            )}
            {isTieredPriceOnApplication && (
              <Box marginTop={1}>
                <TieredPriceOnApplicationPricingLabel supplierFilterKey={selectedSupplier} />
              </Box>
            )}
            <SupplierConnectionLink
              showConnectDistributorLink={showConnectDistributorLink}
              selectedSupplier={selectedDistributor?.distributor}
            />
          </ConnectLinkWrapper>
        }
        Action={
          <ActionButton
            cartItem={cartItem}
            handleAddToCart={handleAddToCart}
            handleQuantityChange={handleQuantityChange}
            bulkOptions={bulkOptions}
            selectedBulkOption={bulkOption}
            setBulkOption={setBulkOption}
            bulkQuantity={bulkQuantity}
            setBulkQuantity={(quantity) => setBulkQuantity(quantity)}
            showConnectDistributorLink={showConnectDistributorLink}
          />
        }
      />
    </ComponentVersionsInherit>
  )
}

export default ProductTile
